import IconCircleCheck from '@/asset/icons/check-icon';
import LikeIcon from '@/asset/icons/like-icon';
import UnavailableIcon from '@/asset/icons/unavailable-icon';
import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { PlanSubscription, PricingPlan } from '@/constants/enum';
import { useGetGeneralSettingQuery, useLazyGetChargeUrlQuery } from '@/redux/query';
import { Badge, BlockStack, Box, Button, Card, InlineStack } from '@shopify/polaris';
import { StyledBadge, StyledBadgeSmall, StyledBox, StyledCard, StyledTextInclude } from './styled';
import { useRedirect } from '@/hooks/useRedirect';

interface Service {
  isAvailable: boolean;
  isInProgress: boolean;
  detail: string;
  isHidden?: boolean;
}

interface PlanCardProps {
  cardItem: {
    plan: PricingPlan;
    subscription: PlanSubscription;
    description: string;
    price: number;
    tag: string;
    isActive: boolean;
    serviceInclue: string;
    listServices: Service[];
    oldPriceMonth?: number;
    oldPriceYear?: number;
    trialDays?: number;
  };
  currentPlan?: string;
}

const PlanCard: React.FC<PlanCardProps> = ({ cardItem, currentPlan }: PlanCardProps) => {
  const [getChargeUrl, { isFetching: isFetchingChargeUrl }] = useLazyGetChargeUrlQuery();
  const { refetch: refetchGeneralSetting } = useGetGeneralSettingQuery({ shop: config.shop });
  const { redirectTo } = useRedirect();

  // const isDowngrade = useMemo(() => {
  //   const UNDER_ENTERPRISE = [PricingPlan.Free, PricingPlan.Essential, PricingPlan.Smart];
  //   const UNDER_SMART = [PricingPlan.Free, PricingPlan.Essential];
  //   const UNDER_ESSENTIAL = [PricingPlan.Free];

  //   switch (currentPlan) {
  //     case PricingPlan.Enterprise:
  //       return UNDER_ENTERPRISE.includes(cardItem.plan);
  //     case PricingPlan.Smart:
  //       return UNDER_SMART.includes(cardItem.plan);
  //     case PricingPlan.Essential:
  //       return UNDER_ESSENTIAL.includes(cardItem.plan);
  //   }
  // }, [cardItem.plan, currentPlan]);

  const handleCharge = () => {
    getChargeUrl({
      shop: config.shop,
      plan: cardItem.plan,
      subscription: cardItem.subscription,
    })
      .unwrap()
      .then((data) => {
        refetchGeneralSetting();
        redirectTo(data.data.url);
      })
      .catch(() => {});
  };

  return (
    <StyledCard>
      <Card>
        <BlockStack align="space-between">
          <BlockStack gap="200">
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <BlockStack inlineAlign="start">
                  <Box
                    paddingBlockStart={'100'}
                    paddingBlockEnd={'100'}
                    paddingInlineStart={'300'}
                    paddingInlineEnd={'300'}
                    background="bg-surface-selected"
                    borderRadius="150"
                    shadow="300"
                  >
                    <BoldText>{cardItem.plan.toUpperCase()}</BoldText>
                  </Box>
                </BlockStack>
                {cardItem.tag && (
                  <InlineStack blockAlign="center" gap="200">
                    <StyledBadge>
                      <Badge tone="success" size="medium">
                        {cardItem.tag.toUpperCase()}
                      </Badge>
                    </StyledBadge>
                    <LikeIcon width={'20'} height={'20'} />
                  </InlineStack>
                )}
              </InlineStack>
            </BlockStack>
            <Box minHeight="60px">
              <RegularText>{cardItem.description}</RegularText>
            </Box>
            <InlineStack gap={'100'}>
              {cardItem.oldPriceMonth && cardItem.subscription === PlanSubscription.Monthly && (
                <BoldText variant="headingLg" textDecorationLine="line-through" tone="disabled">
                  ${cardItem.oldPriceMonth}
                </BoldText>
              )}
              <BoldText variant="headingLg">${cardItem.price}</BoldText>
            </InlineStack>
            <BoldText>{cardItem.subscription === PlanSubscription.Monthly ? 'Per month' : 'Per year'}</BoldText>
          </BlockStack>
          <Box paddingBlockStart="300">
            <BlockStack gap="200">
              {cardItem.serviceInclue && (
                <StyledTextInclude>
                  <BoldSmallText>{cardItem.serviceInclue}</BoldSmallText>
                </StyledTextInclude>
              )}
              {cardItem.listServices.map((service, index) => (
                <div style={{ visibility: service.isHidden ? 'hidden' : 'visible' }} key={index}>
                  <InlineStack align="space-between">
                    <Box paddingBlockStart="050" paddingBlockEnd="050">
                      <InlineStack wrap={false} blockAlign="center" gap="200">
                        {service.isAvailable ? <IconCircleCheck /> : <UnavailableIcon />}
                        <RegularText>{service.detail}</RegularText>
                      </InlineStack>
                    </Box>
                    {service.isInProgress && (
                      <StyledBadgeSmall>
                        <Badge tone="success">Upcoming</Badge>
                      </StyledBadgeSmall>
                    )}
                  </InlineStack>
                </div>
              ))}
            </BlockStack>
          </Box>
          <StyledBox isActive={cardItem.isActive}>
            <Button
              fullWidth
              variant={'primary'}
              disabled={cardItem.isActive}
              onClick={handleCharge}
              loading={isFetchingChargeUrl}
            >
              {cardItem.isActive
                ? 'Your current plan'
                : cardItem.plan === PricingPlan.Free
                ? 'Choose Free'
                : `Start ${cardItem.trialDays}-day trial`}
            </Button>
          </StyledBox>
        </BlockStack>
      </Card>
    </StyledCard>
  );
};

export default PlanCard;
